import PushNotificationsContext from "@/contexts/PushNotificationsContext";
import React, { useContext, useEffect } from "react";
import * as sentry from "@sentry/react";
import axios from "@/axios";
import { unsubscribeUserFromPush } from "@/lib/pushNotifications";
import AuthContext from "@/contexts/AuthContext";

// This function is needed because Chrome doesn't accept a base64 encoded string
// as value for applicationServerKey in pushManager.subscribe yet
// https://bugs.chromium.org/p/chromium/issues/detail?id=802280
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/[-]/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

interface SubscriptionPublicKeyResponse {
  public_key: string;
}

async function getVapidPublicKey() {
  const { data } = await axios.get<SubscriptionPublicKeyResponse>(
    "/bff/api/push-notifications/subscriptions/public-key/"
  );
  return urlBase64ToUint8Array(data.public_key);
}

async function sendSubscriptionToServer({
  subscription,
}: {
  subscription: PushSubscription;
}) {
  await axios.post("/bff/api/push-notifications/subscriptions/", subscription);
}

async function subscribeUserToPush() {
  const registration = await navigator.serviceWorker.register(
    "/service-worker.js"
  );
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: await getVapidPublicKey(),
    });
  }

  await sendSubscriptionToServer({ subscription });
}

async function tryToSubscribeUserToPush() {
  if ("serviceWorker" in navigator) {
    try {
      await subscribeUserToPush();
    } catch (error) {
      sentry.captureException(error);
    }
  }
}

interface UpdateIsSubscribedFromNotificationsPermissionStatusParams {
  permissionStatus: PermissionStatus;
  setIsSubscribed: React.Dispatch<React.SetStateAction<boolean | null>>;
}

function updateIsSubscribedFromNotificationsPermissionStatus({
  permissionStatus,
  setIsSubscribed,
}: UpdateIsSubscribedFromNotificationsPermissionStatusParams) {
  switch (permissionStatus.state) {
    case "granted":
      setIsSubscribed(true);
      break;
    case "denied":
      setIsSubscribed(false);
      break;
    default:
      setIsSubscribed(null);
      break;
  }
}

interface CreateListenerForNotificationPermissionChangeParams {
  setIsSubscribed: React.Dispatch<React.SetStateAction<boolean | null>>;
}

async function createListenerForNotificationPermissionChange({
  setIsSubscribed,
}: CreateListenerForNotificationPermissionChangeParams) {
  if ("permissions" in navigator) {
    const permissionStatus = await navigator.permissions.query({
      name: "notifications",
    });
    updateIsSubscribedFromNotificationsPermissionStatus({
      permissionStatus,
      setIsSubscribed,
    });
    permissionStatus.onchange = () => {
      updateIsSubscribedFromNotificationsPermissionStatus({
        permissionStatus,
        setIsSubscribed,
      });
    };
  } else {
    console.warn("navigator.permissions is not available");
  }
}

export default function PushNotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isSubscribed, setIsSubscribed] = React.useState<boolean | null>(null);
  const isAuthenticated = useContext(AuthContext);

  useEffect(() => {
    createListenerForNotificationPermissionChange({ setIsSubscribed });
  }, []);

  useEffect(() => {
    // Push subscription endpoints require authentication. Skip while the user
    // is on the login page (no token) so the axios interceptor doesn't reject
    // the request and surface an "Um erro inesperado ocorreu" to the user.
    if (!isAuthenticated) {
      return;
    }
    if (isSubscribed === true) {
      tryToSubscribeUserToPush();
    } else if (isSubscribed === false) {
      unsubscribeUserFromPush();
    }
  }, [isSubscribed, isAuthenticated]);

  return (
    <PushNotificationsContext.Provider
      value={{ isSubscribed, setIsSubscribed }}
    >
      {children}
    </PushNotificationsContext.Provider>
  );
}
